import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, X, AlertTriangle } from "lucide-react";

export interface AttachmentInfo {
    id: number;
    notification_id: string;
    original_name: string;
    size: number;
    mime_type: string;
}

interface NotificationAttachmentsProps {
    notificationId: string;
    pendingFiles: File[];
    onPendingFilesChange: (files: File[]) => void;
    existingAttachments: AttachmentInfo[];
    onDeleteExisting: (attachmentId: number) => void;
    mode: 'create' | 'edit';
}

const ACCEPTED_TYPES = ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg";
const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

const NotificationAttachments: React.FC<NotificationAttachmentsProps> = ({
    notificationId,
    pendingFiles,
    onPendingFilesChange,
    existingAttachments,
    onDeleteExisting,
    mode,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const totalSize =
        pendingFiles.reduce((sum, f) => sum + f.size, 0) +
        existingAttachments.reduce((sum, a) => sum + a.size, 0);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const valid = files.filter(f => f.size <= MAX_FILE_SIZE);
        if (valid.length < files.length) {
            // Some files were too large — silently skipped
        }
        onPendingFilesChange([...pendingFiles, ...valid]);
        // Reset input so the same file can be selected again
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removePending = (index: number) => {
        onPendingFilesChange(pendingFiles.filter((_, i) => i !== index));
    };

    const allFiles = [
        ...existingAttachments.map(a => ({
            key: `existing-${a.id}`,
            name: a.original_name,
            size: a.size,
            type: 'existing' as const,
            id: a.id,
        })),
        ...pendingFiles.map((f, i) => ({
            key: `pending-${i}`,
            name: f.name,
            size: f.size,
            type: 'pending' as const,
            index: i,
        })),
    ];

    if (allFiles.length === 0) {
        return (
            <div className="pt-2">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 text-xs text-slate-400 hover:text-primary font-medium transition-colors"
                >
                    <Paperclip className="h-3.5 w-3.5" />
                    Joindre un fichier
                </button>
            </div>
        );
    }

    return (
        <div className="pt-2 space-y-2">
            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                className="hidden"
                onChange={handleFileSelect}
            />

            <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Pièces jointes
                </span>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 text-xs text-primary/70 hover:text-primary font-medium transition-colors"
                >
                    <Paperclip className="h-3 w-3" />
                    Ajouter
                </button>
            </div>

            <div className="space-y-1">
                {allFiles.map(file => (
                    <div
                        key={file.key}
                        className="flex items-center justify-between py-1.5 px-3 bg-slate-50 rounded-lg group"
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <span className="text-xs text-slate-600 font-medium truncate">
                                {file.name}
                            </span>
                            <span className="text-xs text-slate-400 shrink-0">
                                {formatFileSize(file.size)}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                if (file.type === 'existing') {
                                    onDeleteExisting(file.id!);
                                } else {
                                    removePending(file.index!);
                                }
                            }}
                            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all ml-2"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ))}
            </div>

            {totalSize > MAX_FILE_SIZE && (
                <div className="flex items-center gap-2 text-xs text-amber-600 font-medium">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Taille totale: {formatFileSize(totalSize)} (limite recommandée: 3 Mo)
                </div>
            )}
        </div>
    );
};

export default NotificationAttachments;
